import { useState, useCallback } from 'react';
import { formatTimeFromMinutes, getWeekNumber } from '../utils/helpers.ts';
import ScheduleEntry from '../models/ScheduleEntry.ts';

export const useDragToCreate = (isAdmin, options = {}) => {
    const {
        // For ClassSchedulerPage behavior
        onDirectCreate,
        selectedPeriodId,
        setScheduledInstances,
        
        // For ScheduleDisplay behavior
        onModalCreate,
        setSelectedScheduleItem,
        setIsScheduleModalOpen,
        currentYear,
        currentDate, // Add current date to get week information
        timelineContext,
        
        // NEW: Context-specific week range providers
        getContextWeekRange, // Function that returns the appropriate week range for the current context
        
        // NEW: Rooms array for room name lookup
        rooms = [] // Array of room objects for looking up room names by ID
    } = options;

    const [dragToCreateData, setDragToCreateData] = useState({
        isActive: false,
        startTime: null,
        endTime: null,
        roomId: null,
        day: null,
        startPosition: { x: 0, y: 0 },
        currentPosition: { x: 0, y: 0 },
        currentWeekNumber: null, // Add current week tracking
        currentYear: null // Add current year tracking
    });

    // Helper function to get the appropriate week series based on context
    const getDefaultWeekSeries = useCallback(() => {
        if (getContextWeekRange && typeof getContextWeekRange === 'function') {
            // ClassSchedulerPage context - use the visible range or period range
            try {
                const weekRange = getContextWeekRange();
                if (weekRange) {
                    return weekRange;
                }
            } catch (error) {
                console.warn('[DragToCreate] Error getting context week range:', error);
            }
        }

        // ScheduleDisplay context or fallback - use current week
        if (timelineContext?.weekNumber) {
            return String(timelineContext.weekNumber);
        }

        const currentWeekNumber = currentDate ? getWeekNumber(currentDate) : getWeekNumber(new Date());
        return currentWeekNumber.toString();
    }, [getContextWeekRange, currentDate, timelineContext]);

    const handleDragToCreateStart = useCallback((day, roomId, startTimeMinutes, startPosition, currentWeekNumber, currentYear) => {
        if (!isAdmin) return;

        const roundedStartTime = Math.floor(startTimeMinutes / 15) * 15;

        console.log('[DragToCreate] Drag-to-create started:', { day, roomId, startTimeMinutes: roundedStartTime, currentWeekNumber, currentYear });

        setDragToCreateData({
            isActive: true,
            startTime: roundedStartTime,
            endTime: roundedStartTime + 30, // Default to 30 minutes initially
            roomId,
            day,
            startPosition,
            currentPosition: startPosition,
            currentWeekNumber: currentWeekNumber
                || timelineContext?.weekNumber
                || (currentDate ? getWeekNumber(currentDate) : getWeekNumber(new Date())),
            currentYear: currentYear
                || timelineContext?.year
                || (currentDate ? currentDate.getFullYear() : new Date().getFullYear())
        });
    }, [isAdmin, currentDate, timelineContext]);

    const handleDragToCreateMove = useCallback((currentPosition, currentTimeMinutes) => {
        if (!isAdmin || !dragToCreateData.isActive) return;

        // Calculate the end time based on current position
        const endTime = Math.max(
            dragToCreateData.startTime + 15, // Minimum 15 minutes
            Math.round(currentTimeMinutes / 15) * 15 // Round to 15-minute intervals
        );

        setDragToCreateData(prev => ({
            ...prev,
            endTime,
            currentPosition
        }));
    }, [isAdmin, dragToCreateData.isActive, dragToCreateData.startTime]);

    const handleDragToCreateEnd = useCallback(() => {
        if (!isAdmin || !dragToCreateData.isActive) return;

        const { startTime, endTime, roomId, day, currentWeekNumber, currentYear: dragYear } = dragToCreateData;

        // Only create if drag was meaningful (at least 15 minutes)
        if (endTime - startTime >= 5) {
            const startTimeString = formatTimeFromMinutes(startTime);
            const endTimeString = formatTimeFromMinutes(endTime);

            console.log('[DragToCreate] Drag-to-create completed:', {
                day, roomId, startTimeString, endTimeString, currentWeekNumber, currentYear: dragYear
            });

            // Get the appropriate week series for the context
            const defaultWeekSeries = getDefaultWeekSeries();

            if (onDirectCreate) {
                // ClassSchedulerPage behavior - create item directly
                const newInstanceId = `drag-created-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
                const newFullTimeString = `${startTimeString} - ${endTimeString}`;

                const newInstanceData = {
                    id: newInstanceId,
                    day: day,
                    time: newFullTimeString,
                    timeStart: startTimeString,
                    timeEnd: endTimeString,
                    room_id: roomId, // Use room ID instead of room name
                    course: 'New Class', // Default course name
                    teacher: '',
                    groups: [],
                    type: 'class',
                    period_id: selectedPeriodId ? Number(selectedPeriodId) : null,
                    weekSeries: defaultWeekSeries, // Use context-appropriate week series
                    exceptions: [],
                    typeDnd: 'scheduled_item',
                    durationMinutes: endTime - startTime
                };

                const newInstance = new ScheduleEntry(newInstanceData);
                setScheduledInstances(prevInstances => [...prevInstances, newInstance]);

                console.log("New instance created via drag-to-create:", newInstance);
            } else if (onModalCreate) {
                // ScheduleDisplay behavior - open modal for editing
                // Look up room name by ID
                const targetRoom = rooms.find(room => room.id === roomId);
                const roomName = targetRoom?.name || targetRoom?.roomCode || `Room ${roomId}`;
                
                const templateItem = new ScheduleEntry({
                    id: `temp-add-${Date.now()}`,
                    day,
                    timeStart: startTimeString,
                    timeEnd: endTimeString,
                    course: '',
                    teacher: '',
                    groups: [],
                    room: roomName, // Include room name for proper roomObj creation
                    room_id: roomId, // Use room ID instead of room name
                    type: 'class',
                    weekSeries: defaultWeekSeries, // Use context-appropriate week series
                    period_id: selectedPeriodId ? Number(selectedPeriodId) : null,
                    // Store the drag context for the modal to use
                    _dragContext: {
                        currentWeekNumber,
                        currentYear: dragYear,
                        selectedPeriodId,
                        contextWeekRange: defaultWeekSeries // Store the context week range
                    }
                });

                setSelectedScheduleItem(templateItem);
                setIsScheduleModalOpen(true);
            }
        }

        // Reset drag-to-create state
        setDragToCreateData({
            isActive: false,
            startTime: null,
            endTime: null,
            roomId: null,
            day: null,
            startPosition: { x: 0, y: 0 },
            currentPosition: { x: 0, y: 0 },
            currentWeekNumber: null,
            currentYear: null
        });
    }, [
        isAdmin, 
        dragToCreateData, 
        formatTimeFromMinutes, 
        onDirectCreate, 
        onModalCreate,
        selectedPeriodId,
        setScheduledInstances,
        setSelectedScheduleItem,
        setIsScheduleModalOpen,
        currentYear,
        getDefaultWeekSeries
    ]);

    const handleDragToCreateCancel = useCallback(() => {
        setDragToCreateData({
            isActive: false,
            startTime: null,
            endTime: null,
            room: null,
            day: null,
            startPosition: { x: 0, y: 0 },
            currentPosition: { x: 0, y: 0 },
            currentWeekNumber: null,
            currentYear: null
        });
    }, []);

    return {
        dragToCreateData,
        handleDragToCreateStart,
        handleDragToCreateMove,
        handleDragToCreateEnd,
        handleDragToCreateCancel
    };
}; 